import React, { useCallback, useMemo, useState } from 'react';

import { Popover } from 'react-tiny-popover';

import { ArrowDownSLine, InfoIcon } from '@assets/icons';
import Counter from '@components/Counter/Counter';
import DateInput from '@components/DateInput/DateInput';
import DiscountLabel from '@components/ui/labels/discount/DiscountLabel';
import Price from '@components/ui/price';
import {
  ChildDates,
  Guest,
} from '@components/ui/guest-box/guest-box-tab/GuestBox';
import { hasPriceTableData } from '@components/views/tour/components/price-table-modal/hasPriceTableData';
import { useGetTourVariations } from '@components/views/tour/components/tour-selection/TourSelectionRoom/hooks';
import {
  isCompleteTourChildDateInput,
  isValidTourChildDate,
  parseTourChildDate,
} from '@components/views/tour/helpers/childDateValidation';
import CustomIcon from '@components/utils/CustomIcon';
import MilePriceBadge from '@uikit/milePriceBadge';
import { FORM_DATE_FORMAT, formatMomentDate } from '@utils/dateUtil';

import AlternativeStayOptionsList from './AlternativeStayOptionsList';
import PackageReservationActions from './PackageReservationActions';
import PackageSearchInfoPanel from './PackageSearchInfoPanel';
import PackageTemplatePriceTableModal from './PackageTemplatePriceTableModal';
import PackageTemplateSkeleton, {
  PackageVariationSkeleton,
} from './PackageTemplateSkeleton';
import {
  formatCashDiscountBadgeText,
  formatExtraVariationDescription,
  formatTryEquivalentPriceLabel,
  getTryEquivalentPrice,
} from './discountHelpers';
import {
  doesLoadedPackageMatchSelection,
  getEffectivePackageGuestLimit,
  getPackageAvailabilityState,
  isMissingOccupancyPrice,
  isPackageFull,
  isPersonQuotaUnavailable,
  MAX_PACKAGE_GUESTS_PER_ROOM,
  MISSING_OCCUPANCY_PRICE_MESSAGE,
  resolveRemainingPersonQuota,
  resolveSelectedHotelDetailId,
  shouldHideCallUsPrice,
  shouldSkipTourPrecalculate,
} from './guestLimits';
import {
  ChildDatesWrapper,
  Container,
  ContentStack,
  FullPackageCallButton,
  GuestInfo,
  GuestInfoMessage,
  GuestPanel,
  GuestPopoverContent,
  GuestRow,
  GuestSubtitle,
  GuestTitle,
  Panel,
  PackageUtilityRow,
  ReserveButton,
  OccupiedAvailabilityBox,
  OccupiedAvailabilityText,
  SearchInfoColumn,
  SelectionChevron,
  SelectionContentStack,
  SelectionFieldLabel,
  SelectionLabel,
  SelectionTrigger,
  SummaryGrid,
  VariationCard,
  VariationDescription,
  VariationHeader,
  VariationInfo,
  VariationPriceBlock,
  VariationPriceNote,
  TryEquivalentPrice,
  VariationTitle,
  WarningText,
} from './styled';
import { PACKAGE_TEMPLATE_POPOVER_Z_INDEX } from './constants';
import { PackageTemplateContainerProps } from './types';

const DEFAULT_PROMOTION_INFO = {
  promotionDescription: 'Sezon İndirimli',
  promotionName: 'Standart Paket',
};

const MAX_ADULTS_PER_ROOM = 3;
const MAX_CHILDREN_PER_ROOM = 2;

type DraftChildDate = ChildDates & {
  inputValue: string;
};

const PackageTemplateContainer = ({
  packageSelection,
  selectedPackageId,
  selectedPackageDetailId,
  selectedPackageSelectionDetailId,
  packageDetail,
  packageDetailLoading,
  packageDetailError,
  dayCount,
  nightCount,
  onPackageChange,
  onStayOptionChange,
  onReserve,
  onLetUsCallYou,
  reserveDisabled,
  reserveLoading = false,
}: PackageTemplateContainerProps) => {
  const [isPriceTableOpen, setIsPriceTableOpen] = useState(false);
  const [isGuestPopoverOpen, setIsGuestPopoverOpen] = useState(false);
  const [guests, setGuests] = useState<Guest[]>([{ id: 0, adultNumber: 2 }]);
  const [draftAdultCount, setDraftAdultCount] = useState(2);
  const [draftChildren, setDraftChildren] = useState<DraftChildDate[]>([]);

  const selectedPackage = useMemo(
    () =>
      packageSelection.find(
        (item) =>
          item.id === selectedPackageId &&
          (item.detailId ?? undefined) === selectedPackageSelectionDetailId
      ) ||
      packageSelection.find((item) => item.id === selectedPackageId) ||
      packageSelection[0],
    [packageSelection, selectedPackageId, selectedPackageSelectionDetailId]
  );
  const hotels = useMemo(
    () => packageDetail?.hotels ?? [],
    [packageDetail?.hotels]
  );
  const hotelDetailIds = useMemo(
    () => hotels.map((item) => item.packageDetailId),
    [hotels]
  );
  const currentPackageMatchesSelection = doesLoadedPackageMatchSelection(
    selectedPackageId,
    {
      tourPackageId: packageDetail?.tourPackageId,
      defaultPackageDetailId:
        packageDetail?.defaultPackageDetail?.packageDetailId,
      hotelDetailIds,
    }
  );
  const resolvedSelectedHotelDetailId = resolveSelectedHotelDetailId(
    selectedPackageDetailId,
    hotelDetailIds
  );
  const selectedHotel = hotels.find(
    (item) => item.packageDetailId === resolvedSelectedHotelDetailId
  );
  const defaultPackageDetailId =
    packageDetail?.defaultPackageDetail?.packageDetailId;
  const activePackageDetailId = currentPackageMatchesSelection
    ? resolvedSelectedHotelDetailId ??
      defaultPackageDetailId ??
      selectedPackage?.detailId ??
      0
    : 0;
  const activeHotel = currentPackageMatchesSelection
    ? selectedHotel ??
      hotels.find((item) => item.packageDetailId === defaultPackageDetailId) ??
      hotels[0]
    : undefined;

  const legacyRemainingPersonQuota = packageDetail?.defaultPackageDetail
    ? Math.max(
        packageDetail.defaultPackageDetail.personQuota -
          packageDetail.defaultPackageDetail.personQuotaUsed,
        0
      )
    : undefined;
  const remainingPersonQuota = resolveRemainingPersonQuota(
    activeHotel?.remainingPersonQuota,
    selectedPackage?.remainingPersonQuota,
    legacyRemainingPersonQuota
  );
  const maxPricedGuestCount = useMemo(() => {
    const priceRows =
      packageDetail?.defaultPackageDetail?.roomPrices?.prices ?? [];
    const pricedGuestCounts = priceRows
      .map((item) => item.personCount)
      .filter((personCount) => Number.isFinite(personCount) && personCount > 0);

    return pricedGuestCounts.length
      ? Math.max(...pricedGuestCounts)
      : MAX_PACKAGE_GUESTS_PER_ROOM;
  }, [packageDetail?.defaultPackageDetail?.roomPrices?.prices]);
  const effectiveGuestLimit =
    getEffectivePackageGuestLimit(remainingPersonQuota);
  const adultGuestLimit = Math.max(
    1,
    Math.min(MAX_ADULTS_PER_ROOM, effectiveGuestLimit)
  );
  const guestLimitMessage =
    effectiveGuestLimit === 0
      ? 'Bu paket için uygun kontenjan bulunmamaktadır.'
      : remainingPersonQuota !== undefined &&
        remainingPersonQuota === effectiveGuestLimit
      ? `Kalan kontenjan en fazla ${effectiveGuestLimit} kişi için uygundur.`
      : maxPricedGuestCount === effectiveGuestLimit
      ? `Bu paket için en fazla ${effectiveGuestLimit} kişilik fiyat planı bulunmaktadır.`
      : `Tur’a en fazla ${effectiveGuestLimit} kişi alınabilmektedir.`;
  const isFull = isPackageFull(packageDetail?.defaultPackageDetail || {});
  const availabilityInput = {
    remainingPersonQuota,
    isOccupied: selectedPackage?.isOccupied || activeHotel?.isOccupied,
    tourAvailabilityStatuses: [
      selectedPackage?.tourAvailabilityStatus,
      activeHotel?.tourAvailabilityStatus,
      packageDetail?.defaultPackageDetail?.tourAvailabilityStatus,
    ],
    isFull,
    personQuota: packageDetail?.defaultPackageDetail?.personQuota,
    personQuotaUsed: packageDetail?.defaultPackageDetail?.personQuotaUsed,
    roomQuota: packageDetail?.defaultPackageDetail?.roomQuota,
    roomQuotaUsed: packageDetail?.defaultPackageDetail?.roomQuotaUsed,
  };
  const availabilityState = getPackageAvailabilityState(availabilityInput);
  const shouldShowPriceTable = hasPriceTableData(packageDetail);
  const precalculateAvailabilityInput = {
    remainingPersonQuota,
    tourAvailabilityStatus:
      activeHotel?.tourAvailabilityStatus ??
      packageDetail?.defaultPackageDetail?.tourAvailabilityStatus,
    tourAvailabilityStatuses: [
      selectedPackage?.tourAvailabilityStatus,
      packageDetail?.defaultPackageDetail?.tourAvailabilityStatus,
    ],
    isOccupied:
      activeHotel?.isOccupied === true || selectedPackage?.isOccupied === true,
    isFull,
    personQuota: packageDetail?.defaultPackageDetail?.personQuota,
    personQuotaUsed: packageDetail?.defaultPackageDetail?.personQuotaUsed,
    roomQuota: packageDetail?.defaultPackageDetail?.roomQuota,
    roomQuotaUsed: packageDetail?.defaultPackageDetail?.roomQuotaUsed,
  };
  const skipTourPrecalculate = shouldSkipTourPrecalculate(
    currentPackageMatchesSelection,
    precalculateAvailabilityInput
  );
  const guest = guests[0];
  const guestSummary = useMemo(() => {
    const summary = [`${guest?.adultNumber || 0} Yetişkin`];
    const childCount = guest?.childList?.length || 0;
    if (childCount) summary.push(`${childCount} Çocuk`);
    return summary.join(', ');
  }, [guest]);

  const initializeDraftGuests = useCallback(() => {
    const currentGuest = guests[0];
    const adultCount = Math.min(
      Math.max(currentGuest?.adultNumber || 1, 1),
      adultGuestLimit
    );
    const childList = (currentGuest?.childList || []).slice(
      0,
      Math.min(
        MAX_CHILDREN_PER_ROOM,
        Math.max(effectiveGuestLimit - adultCount, 0)
      )
    );

    setDraftAdultCount(adultCount);
    setDraftChildren(
      childList.map((child) => ({
        ...child,
        inputValue: formatMomentDate(child.date, FORM_DATE_FORMAT),
        error: !child.date,
      }))
    );
  }, [adultGuestLimit, effectiveGuestLimit, guests]);

  const occupancyPrices = useMemo(() => {
    const priceRows =
      packageDetail?.defaultPackageDetail?.roomPrices?.prices ?? [];

    return {
      price1: priceRows.find((item) => item.personCount === 1)?.price,
      price3: priceRows.find((item) => item.personCount === 3)?.price,
    };
  }, [packageDetail?.defaultPackageDetail?.roomPrices?.prices]);
  const isAppliedGuestMissingOccupancyPrice = isMissingOccupancyPrice(
    guest?.adultNumber ?? 2,
    guest?.childList?.length ?? 0,
    occupancyPrices
  );
  const {
    tourDetailVariations,
    showGuestsWarning,
    showGuestsWarningPrice,
    isError: isPrecalculateError,
    isLoading: isPrecalculateLoading,
  } = useGetTourVariations(
    packageDetail?.tourId || 0,
    activePackageDetailId,
    guest,
    effectiveGuestLimit,
    occupancyPrices.price3,
    !skipTourPrecalculate && !isAppliedGuestMissingOccupancyPrice
  );
  const standardPrecalculateVariation =
    tourDetailVariations.find((variation) => variation.isDefault) ||
    tourDetailVariations[0];
  const closedDateDropdownPrice =
    standardPrecalculateVariation?.originalTotalDiscountedSellingPrice;
  const isSelectedPackageUnavailable = isPersonQuotaUnavailable(
    selectedPackage || {}
  );
  const hideClosedDateDropdownPrice =
    availabilityState !== 'available' ||
    isSelectedPackageUnavailable ||
    isPrecalculateLoading ||
    closedDateDropdownPrice == null ||
    tourDetailVariations.length === 0;

  const draftGuestTotal = draftAdultCount + draftChildren.length;
  const hasInvalidDraftChild = draftChildren.some(
    (child) => !isValidTourChildDate(child.inputValue)
  );
  const isDraftGuestMissingOccupancyPrice = isMissingOccupancyPrice(
    draftAdultCount,
    draftChildren.length,
    occupancyPrices
  );

  const handleAdultIncrement = useCallback(() => {
    setDraftAdultCount((current) =>
      current + draftChildren.length >= effectiveGuestLimit
        ? current
        : Math.min(current + 1, adultGuestLimit)
    );
  }, [adultGuestLimit, draftChildren.length, effectiveGuestLimit]);

  const handleAdultDecrement = useCallback(() => {
    setDraftAdultCount((current) => Math.max(current - 1, 1));
  }, []);

  const handleChildIncrement = useCallback(() => {
    setDraftChildren((current) => {
      if (
        current.length >= MAX_CHILDREN_PER_ROOM ||
        draftAdultCount + current.length >= effectiveGuestLimit
      ) {
        return current;
      }

      return [
        ...current,
        {
          id: Date.now() + current.length,
          date: null,
          error: false,
          inputValue: '',
        },
      ];
    });
  }, [draftAdultCount, effectiveGuestLimit]);

  const handleChildDecrement = useCallback(() => {
    setDraftChildren((current) => current.slice(0, -1));
  }, []);

  const handleChildDatePick = useCallback(
    (childId: number, dateStr: string) => {
      const date = parseTourChildDate(dateStr);
      const isCompleteDate = isCompleteTourChildDateInput(dateStr);
      setDraftChildren((current) =>
        current.map((child) =>
          child.id === childId
            ? {
                ...child,
                date,
                error: dateStr.length === 0 || (isCompleteDate && !date),
                inputValue: dateStr,
              }
            : child
        )
      );
    },
    []
  );

  const applyGuestSelection = useCallback(() => {
    if (
      hasInvalidDraftChild ||
      draftGuestTotal > effectiveGuestLimit ||
      isDraftGuestMissingOccupancyPrice
    ) {
      setDraftChildren((current) =>
        current.map((child) => ({
          ...child,
          error: !child.date,
        }))
      );
      return;
    }

    setGuests([
      {
        id: 0,
        adultNumber: draftAdultCount,
        childList: draftChildren.length
          ? draftChildren.map(({ id, date }) => ({ id, date, error: false }))
          : undefined,
      },
    ]);

    setIsGuestPopoverOpen(false);
  }, [
    draftAdultCount,
    draftChildren,
    draftGuestTotal,
    effectiveGuestLimit,
    hasInvalidDraftChild,
    isDraftGuestMissingOccupancyPrice,
  ]);

  const variations = useMemo(
    () => tourDetailVariations,
    [tourDetailVariations]
  );
  const supportedVariations = variations.filter(
    (variation) => !variation.loyaltyType
  );

  const reserveVariation = useCallback(
    (variation: (typeof variations)[number]) => {
      if (activePackageDetailId) {
        onReserve?.(variation, activePackageDetailId, guests);
      }
    },
    [activePackageDetailId, guests, onReserve]
  );

  const isPackageDetailLoading = packageDetailLoading && !packageDetailError;
  const packageDetailStatus = packageDetailError
    ? 'Paket bilgileri alınamadı. Lütfen tekrar deneyin veya başka bir paket seçin.'
    : packageDetailLoading
    ? 'Paket bilgileri yükleniyor...'
    : isPrecalculateLoading
    ? 'Fiyat bilgisi yükleniyor...'
    : 'Seçilen paket için uygun detay bulunamadı.';
  const guestSelectionPanel = (
    <GuestPanel $embedded>
      <Popover
        isOpen={isGuestPopoverOpen}
        positions={['bottom']}
        align='start'
        padding={4}
        containerStyle={{ zIndex: String(PACKAGE_TEMPLATE_POPOVER_Z_INDEX) }}
        onClickOutside={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest?.('.rc-tooltip, [role="tooltip"]')) {
            return;
          }
          setIsGuestPopoverOpen(false);
        }}
        content={({ childRect }) => (
          <GuestPopoverContent
            $width={childRect.width}
            role='dialog'
            aria-label='Kişi sayısı seçimi'
            onMouseDown={(event) => event.stopPropagation()}
          >
            <GuestRow>
              <GuestInfo>
                <GuestTitle>Yetişkin</GuestTitle>
                <GuestSubtitle>12+ Yaş</GuestSubtitle>
              </GuestInfo>
              <Counter
                count={draftAdultCount}
                min={1}
                max={adultGuestLimit}
                handleIncrement={handleAdultIncrement}
                handleDecrement={handleAdultDecrement}
                disabledIncrement={
                  draftAdultCount >= adultGuestLimit ||
                  draftGuestTotal >= effectiveGuestLimit
                }
                tooltipMessage={guestLimitMessage}
              />
            </GuestRow>
            <GuestRow>
              <GuestInfo>
                <GuestTitle>Çocuk</GuestTitle>
                <GuestSubtitle>0-12 Yaş</GuestSubtitle>
              </GuestInfo>
              <Counter
                count={draftChildren.length}
                min={0}
                max={MAX_CHILDREN_PER_ROOM}
                handleIncrement={handleChildIncrement}
                handleDecrement={handleChildDecrement}
                disabledIncrement={
                  draftChildren.length >= MAX_CHILDREN_PER_ROOM ||
                  draftGuestTotal >= effectiveGuestLimit
                }
                tooltipMessage={guestLimitMessage}
              />
            </GuestRow>
            {draftChildren.length ? (
              <ChildDatesWrapper>
                {draftChildren.map((child, index) => (
                  <DateInput
                    key={child.id}
                    date={child.inputValue}
                    label={`${index + 1}. Çocuk Doğum Tarihi`}
                    error={Boolean(child.error)}
                    isChild
                    maxAge={12}
                    handleDatePick={(_, value) =>
                      handleChildDatePick(child.id, value)
                    }
                  />
                ))}
              </ChildDatesWrapper>
            ) : null}
            {draftAdultCount === 1 && draftChildren.length ? (
              <GuestInfoMessage>
                <CustomIcon
                  width={20}
                  height={20}
                  paletteColor='B300'
                  icon={InfoIcon}
                />
                <span>
                  Tek yetişkin seçiminde çocuk fiyatı{' '}
                  <strong>geçersizdir</strong>, yetişkin olarak hesaplanacaktır.
                </span>
              </GuestInfoMessage>
            ) : null}
            {isDraftGuestMissingOccupancyPrice ? (
              <GuestInfoMessage>
                <CustomIcon
                  width={20}
                  height={20}
                  paletteColor='B300'
                  icon={InfoIcon}
                />
                <span>{MISSING_OCCUPANCY_PRICE_MESSAGE}</span>
              </GuestInfoMessage>
            ) : null}
            {draftGuestTotal >= effectiveGuestLimit &&
            !isDraftGuestMissingOccupancyPrice ? (
              <GuestInfoMessage>
                <CustomIcon
                  width={20}
                  height={20}
                  paletteColor='B300'
                  icon={InfoIcon}
                />
                <span>{guestLimitMessage}</span>
              </GuestInfoMessage>
            ) : null}
            <PackageReservationActions
              ctaLabel='Uygula'
              disabled={
                hasInvalidDraftChild ||
                draftGuestTotal > effectiveGuestLimit ||
                isDraftGuestMissingOccupancyPrice
              }
              onReserve={applyGuestSelection}
            />
          </GuestPopoverContent>
        )}
      >
        <SelectionTrigger
          type='button'
          $selected
          aria-haspopup='dialog'
          aria-expanded={isGuestPopoverOpen}
          onClick={() => {
            if (!isGuestPopoverOpen) initializeDraftGuests();
            setIsGuestPopoverOpen((current) => !current);
          }}
        >
          <SelectionContentStack>
            <SelectionFieldLabel>Kişi Sayısı</SelectionFieldLabel>
            <SelectionLabel>{guestSummary}</SelectionLabel>
          </SelectionContentStack>
          <SelectionChevron aria-hidden='true'>
            <CustomIcon
              icon={ArrowDownSLine}
              fontSize={20}
              paletteColor='grey700'
            />
          </SelectionChevron>
        </SelectionTrigger>
      </Popover>
      {showGuestsWarning ? (
        <WarningText>{guestLimitMessage}</WarningText>
      ) : null}
      {showGuestsWarningPrice ? (
        <WarningText>
          Bu kişi sayısı için uygun fiyat planı bulunamadı.
        </WarningText>
      ) : null}
      {isPrecalculateError ? (
        <WarningText>Fiyat bilgisi alınamadı.</WarningText>
      ) : null}
    </GuestPanel>
  );

  if (!packageSelection.length) return null;

  return (
    <>
      <Container
        aria-busy={isPackageDetailLoading}
        aria-label={
          isPackageDetailLoading ? 'Paket bilgileri yükleniyor' : undefined
        }
      >
        <SummaryGrid>
          <SearchInfoColumn>
            <PackageSearchInfoPanel
              key={`${selectedPackageId ?? 'default'}-${
                selectedPackageSelectionDetailId ?? 'default'
              }`}
              packages={packageSelection}
              selectedPackageId={selectedPackageId}
              selectedPackageDetailId={selectedPackageDetailId}
              selectedPackageSelectionDetailId={
                selectedPackageSelectionDetailId
              }
              precalculatedPrice={closedDateDropdownPrice}
              precalculatedCurrencyCode={
                standardPrecalculateVariation?.originalCurrency ||
                packageDetail?.currencyCode
              }
              hidePrecalculatedPrice={hideClosedDateDropdownPrice}
              isLoading={isPackageDetailLoading}
              dayCount={dayCount}
              nightCount={nightCount}
              packageDetail={packageDetail}
              selectedPackageAvailability={
                currentPackageMatchesSelection ? availabilityInput : undefined
              }
              onPackageChange={onPackageChange}
            >
              {packageDetail ? (
                <>
                  {guestSelectionPanel}
                  <AlternativeStayOptionsList
                    embedded
                    hotels={hotels}
                    selectedPackageDetailId={activePackageDetailId}
                    currencyCode={packageDetail.currencyCode}
                    onStayOptionChange={onStayOptionChange}
                  />
                </>
              ) : null}
            </PackageSearchInfoPanel>
          </SearchInfoColumn>
          <ContentStack>
            {isPackageDetailLoading ? (
              <PackageTemplateSkeleton />
            ) : packageDetail ? (
              <>
                <PackageUtilityRow data-testid='package-utility-row'>
                  {shouldShowPriceTable ? (
                    <ReserveButton
                      type='button'
                      onClick={() => setIsPriceTableOpen(true)}
                    >
                      Fiyat Tablosu
                    </ReserveButton>
                  ) : null}
                </PackageUtilityRow>

                {availabilityState === 'occupied' ? (
                  <VariationCard
                    key='occupied-package'
                    aria-label='Konaklama müsait değil'
                  >
                    <VariationHeader>
                      <VariationInfo>
                        <VariationTitle>Standart Paket</VariationTitle>
                        <VariationDescription>
                          {DEFAULT_PROMOTION_INFO.promotionDescription}
                        </VariationDescription>
                      </VariationInfo>
                    </VariationHeader>
                    <OccupiedAvailabilityBox>
                      <OccupiedAvailabilityText>
                        Müsaitlik bulunmamaktadır.
                      </OccupiedAvailabilityText>
                    </OccupiedAvailabilityBox>
                  </VariationCard>
                ) : availabilityState === 'callUs' ? (
                  (() => {
                    const callUsVariation =
                      supportedVariations.find(
                        (variation) => variation.isDefault
                      ) || supportedVariations[0];
                    const callUsPrice =
                      callUsVariation?.originalTotalDiscountedSellingPrice ??
                      callUsVariation?.originalTotalSellingPrice;
                    const hasCallUsPrice =
                      callUsVariation != null &&
                      callUsPrice != null &&
                      callUsPrice > 0;
                    const callUsCurrency =
                      callUsVariation?.originalCurrency ||
                      packageDetail.currencyCode ||
                      'TL';
                    const tryEquivalentPrice = hasCallUsPrice
                      ? getTryEquivalentPrice(callUsVariation)
                      : null;
                    const hideCallUsPrice =
                      shouldHideCallUsPrice(availabilityInput);

                    return (
                      <VariationCard key='call-us-package'>
                        <VariationHeader>
                          <VariationInfo>
                            <VariationTitle>Standart Paket</VariationTitle>
                          </VariationInfo>
                        </VariationHeader>
                        {hasCallUsPrice && !hideCallUsPrice ? (
                          <VariationPriceBlock>
                            {callUsVariation?.milePrice > 0 ? (
                              <MilePriceBadge
                                milePrice={callUsVariation.milePrice}
                              />
                            ) : null}
                            <Price
                              price={callUsPrice}
                              currency={callUsCurrency}
                              isTour
                            />
                            {tryEquivalentPrice ? (
                              <TryEquivalentPrice>
                                {formatTryEquivalentPriceLabel(
                                  tryEquivalentPrice
                                )}
                              </TryEquivalentPrice>
                            ) : null}
                            <VariationPriceNote>
                              Toplam Fiyat
                            </VariationPriceNote>
                          </VariationPriceBlock>
                        ) : null}
                        {onLetUsCallYou ? (
                          <FullPackageCallButton
                            type='button'
                            onClick={() => onLetUsCallYou()}
                          >
                            Biz Sizi Arayalım
                          </FullPackageCallButton>
                        ) : null}
                      </VariationCard>
                    );
                  })()
                ) : isAppliedGuestMissingOccupancyPrice ? (
                  <VariationCard
                    key='missing-occupancy-price'
                    aria-label='Seçilen kişi sayısı için fiyat bulunamadı'
                  >
                    <VariationHeader>
                      <VariationInfo>
                        <VariationTitle>Standart Paket</VariationTitle>
                      </VariationInfo>
                    </VariationHeader>
                    <OccupiedAvailabilityBox>
                      <OccupiedAvailabilityText>
                        {MISSING_OCCUPANCY_PRICE_MESSAGE}
                      </OccupiedAvailabilityText>
                    </OccupiedAvailabilityBox>
                  </VariationCard>
                ) : isPrecalculateLoading ? (
                  <>
                    <PackageVariationSkeleton />
                    <PackageVariationSkeleton />
                  </>
                ) : supportedVariations.length ? (
                  supportedVariations.map((variation, index) => {
                    const promotionInfo = variation.promotionInfoList?.[0];
                    const hasDiscount =
                      variation.originalTotalSellingPrice >
                      variation.originalTotalDiscountedSellingPrice;
                    const discountAmount = hasDiscount
                      ? (
                          variation.originalTotalSellingPrice -
                          variation.originalTotalDiscountedSellingPrice
                        ).toFixed(0)
                      : null;
                    const isExtraVariation = !variation.isDefault;
                    const packageName =
                      promotionInfo?.promotionName ||
                      DEFAULT_PROMOTION_INFO.promotionName;
                    const promotionDescription = isExtraVariation
                      ? formatExtraVariationDescription(
                          promotionInfo?.rateAmountValue,
                          promotionInfo?.rateAmountType,
                          promotionInfo?.rateAmountCurrencyCode ||
                            variation.originalCurrency
                        )
                      : promotionInfo?.promotionDescription ||
                        DEFAULT_PROMOTION_INFO.promotionDescription;
                    const variationCurrency =
                      variation.originalCurrency ||
                      packageDetail.currencyCode ||
                      'TL';
                    const tryEquivalentPrice = getTryEquivalentPrice(variation);
                    const cashDiscountBadgeText = !isExtraVariation
                      ? formatCashDiscountBadgeText(
                          {
                            cashDiscountRateAmountType:
                              variation.cashDiscountRateAmountType,
                            cashDiscountRateAmountValue:
                              variation.cashDiscountRateAmountValue,
                            cashDiscountCurrencyName:
                              variation.cashDiscountCurrencyName,
                            originalCurrency: variation.originalCurrency,
                          },
                          variationCurrency
                        ) ||
                        (discountAmount
                          ? `${discountAmount} ${variationCurrency} İndirim`
                          : null)
                      : null;

                    return (
                      <VariationCard key={variation.variationId || index}>
                        <VariationHeader>
                          <VariationInfo>
                            <VariationTitle>{packageName}</VariationTitle>
                            {promotionDescription ? (
                              <VariationDescription>
                                {promotionDescription}
                              </VariationDescription>
                            ) : null}
                          </VariationInfo>
                        </VariationHeader>
                        <VariationPriceBlock>
                          {cashDiscountBadgeText ? (
                            <DiscountLabel
                              discount={cashDiscountBadgeText}
                              type='text'
                            />
                          ) : null}
                          {variation.milePrice > 0 ? (
                            <MilePriceBadge milePrice={variation.milePrice} />
                          ) : null}
                          <Price
                            price={
                              variation.originalTotalDiscountedSellingPrice
                            }
                            currency={variationCurrency}
                            originalPrice={
                              isExtraVariation
                                ? undefined
                                : variation.originalTotalSellingPrice
                            }
                            originalPriceCurrency={variationCurrency}
                            isTour
                          />
                          {tryEquivalentPrice ? (
                            <TryEquivalentPrice>
                              {formatTryEquivalentPriceLabel(
                                tryEquivalentPrice
                              )}
                            </TryEquivalentPrice>
                          ) : null}
                          <VariationPriceNote>Toplam Fiyat</VariationPriceNote>
                        </VariationPriceBlock>
                        {onReserve ? (
                          <PackageReservationActions
                            disabled={
                              reserveDisabled ||
                              isFull ||
                              packageDetailLoading ||
                              isPrecalculateLoading ||
                              showGuestsWarning ||
                              showGuestsWarningPrice
                            }
                            loading={reserveLoading}
                            ctaLabel='Turu Seç'
                            onReserve={() => reserveVariation(variation)}
                          />
                        ) : null}
                      </VariationCard>
                    );
                  })
                ) : (
                  <VariationCard
                    key='occupied-package-empty-variations'
                    aria-label='Konaklama müsait değil'
                  >
                    <VariationHeader>
                      <VariationInfo>
                        <VariationTitle>Standart Paket</VariationTitle>
                        <VariationDescription>
                          {DEFAULT_PROMOTION_INFO.promotionDescription}
                        </VariationDescription>
                      </VariationInfo>
                    </VariationHeader>
                    <OccupiedAvailabilityBox>
                      <OccupiedAvailabilityText>
                        Müsaitlik bulunmamaktadır.
                      </OccupiedAvailabilityText>
                    </OccupiedAvailabilityBox>
                  </VariationCard>
                )}
              </>
            ) : (
              <Panel>
                <WarningText>{packageDetailStatus}</WarningText>
              </Panel>
            )}
          </ContentStack>
        </SummaryGrid>
      </Container>

      <PackageTemplatePriceTableModal
        isOpen={isPriceTableOpen && shouldShowPriceTable}
        onClose={() => setIsPriceTableOpen(false)}
        packageData={packageDetail}
      />
    </>
  );
};

export default PackageTemplateContainer;
